import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ParametresToit,
  TypeStyleBaleine,
  TypeMateriauToit,
  TypePointJonction,
  ProfilBaleinePersonnalise,
  OutilDessin,
  StyleBaleinePredefini,
  DonneesStructureBasePourToit,
  StructureToit,
  EvenementCanvas,
  EtatModuleToit
} from '../types';
import { Toit3DService } from '../services/Toit3DService';
import { Canvas2DService } from '../services/Canvas2DService';
// API supprimée - génération locale obligatoire selon cahier des charges
import './ToitModule.css';
import { ModalEditeurProfil } from './ModalEditeurProfil';

interface ToitModuleProps {
  donneesStructureBase: DonneesStructureBasePourToit;
  onStructureChange: (structure: StructureToit | null) => void;
  onParametresChange: (parametres: ParametresToit) => void;
  parametresInitiaux?: ParametresToit;
  actif?: boolean;
}

/**
 * Composant principal du module Toit
 * Conforme au cahier des charges - Module 4: Le Toit de la Tonnelle
 * 
 * FONCTIONNALITÉS IMPLÉMENTÉES:
 * - Tâche 4.1: Génération des Nervures du Toit ("Baleines")
 * - Tâche 4.2: Mécanisme de Génération du Toit
 * - Tâche 4.3: Bibliothèque de Styles Prédéfinis
 * - Tâche 4.4: Outil de Dessin Personnalisé (Canvas 2D)
 * - Tâche 4.5: Adaptation Dynamique Cruciale de la Longueur des Baleines
 * 
 * GÉNÉRATION LOCALE OBLIGATOIRE:
 * - Élimination des clignotements
 * - Suppression des résidus fantômes
 * - Réactivité instantanée
 * - Expérience utilisateur fluide
 */
export const ToitModule: React.FC<ToitModuleProps> = ({
  donneesStructureBase,
  onStructureChange,
  onParametresChange,
  parametresInitiaux,
  actif = true
}) => {
  // Services
  const toit3DService = useRef(new Toit3DService());
  const canvas2DService = useRef(new Canvas2DService({
    largeur: 400,
    hauteur: 300,
    couleurFond: '#f8f9fa',
    couleurGrille: '#e9ecef',
    couleurDessin: '#007bff',
    couleurSelection: '#28a745',
    epaisseurLigne: 2,
    taillePoint: 4,
    afficherGrille: true,
    magnetisme: true,
    toleranceSelection: 10
  }));

  // Fonction de log simplifiée pour éviter le lag
  const ecrireDansFichierLogs = useCallback((message: string) => {
    // Log simple dans la console uniquement
    console.log(`[TOIT] ${message}`);
  }, []);

  // État du module
  const [etat, setEtat] = useState<EtatModuleToit>({
    enChargement: false,
    parametres: parametresInitiaux || {
      actif: false, // Désactivé par défaut
      styleBaleine: TypeStyleBaleine.COURBE_DOUCE,
      nombreBaleines: donneesStructureBase.nombreMontants,
      hauteurToit: 1.2,
      hauteurConvergence: 1.5,
      pointJonction: TypePointJonction.POINT_SIMPLE,
      rayonPointJonction: 0.1,
      aplatirBaleines: true,
      alignerSurMontants: false
    },
    structureGeneree: false,
    stylesPredefinis: [],
    profilsPersonnalises: [],
    modeEdition: false,
    outilCanvas: OutilDessin.CRAYON,
    profilEnCours: undefined,
    erreurs: [],
    avertissements: []
  });

  // Références
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvasInitialise, setCanvasInitialise] = useState(false);
const [modalOuverte, setModalOuverte] = useState(false);

  /**
   * Génération locale des styles prédéfinis
   */
  const obtenirStylesPredefinis = useCallback(() => {
    return [
      {
        id: TypeStyleBaleine.CLASSIQUE,
        nom: 'Classique',
        description: 'Style élégant et intemporel',
        apercu: '/images/styles/classique.svg',
        profil2D: []
      },
      {
        id: TypeStyleBaleine.COURBE_DOUCE,
        nom: 'Courbe Douce',
        description: 'Courbe harmonieuse et élégante',
        apercu: '/images/styles/courbe-douce.svg',
        profil2D: []
      },
      {
        id: TypeStyleBaleine.GOTHIQUE,
        nom: 'Gothique',
        description: 'Style architectural gothique pointu',
        apercu: '/images/styles/gothique.svg',
        profil2D: []
      },
      {
        id: TypeStyleBaleine.MODERNE,
        nom: 'Moderne',
        description: 'Lignes épurées et contemporaines',
        apercu: '/images/styles/moderne.svg',
        profil2D: []
      },
      {
        id: TypeStyleBaleine.ASIATIQUE,
        nom: 'Asiatique',
        description: 'Inspiration pagode et architecture asiatique',
        apercu: '/images/styles/asiatique.svg',
        profil2D: []
      }
    ];
  }, []);

   /**
    * Gestion locale des profils personnalisés
    */
   const gererProfilsPersonnalises = useCallback(() => {
  // Stockage local des profils personnalisés (conforme au cahier des charges)
  const profilsStockes = localStorage.getItem('toit-profils-personnalises');
  if (profilsStockes) {
    try {
      const parsed = JSON.parse(profilsStockes);
      return parsed.map((p: any) => {
  const base = p.profil ? p.profil : p;
  return {
     id: p.id || base.id || Date.now().toString(),
     nom: p.nom || base.nom || 'Profil sans nom',
     largeur: base.largeur ?? 1,
     hauteur: base.hauteur ?? 1,
     courbes: base.courbes ?? [],
     lignes: base.lignes ?? [],
     lignesDroites: base.lignesDroites ?? [],
     profil: base.profil ?? [],
     dateCreation: p.dateCreation || base.dateCreation || new Date().toISOString()
   } as ProfilBaleinePersonnalise & {dateCreation: string};
});
    } catch (error) {
      console.warn('Erreur lors du chargement des profils personnalisés:', error);
    }
  }
  return [];
}, []);

   /**
    * Sauvegarde locale d'un profil personnalisé
    */
   const sauvegarderProfilPersonnalise = useCallback((nom: string, profil?: ProfilBaleinePersonnalise) => {
     const profils = gererProfilsPersonnalises();
     if (!profil) return;
     const nouveauProfil = {
       ...profil,
       id: Date.now().toString(),
       nom,
       dateCreation: new Date().toISOString()
     } as ProfilBaleinePersonnalise & {dateCreation: string};
     profils.push(nouveauProfil);
     localStorage.setItem('toit-profils-personnalises', JSON.stringify(profils));

     setEtat(prev => ({
       ...prev,
       profilsPersonnalises: [...prev.profilsPersonnalises, nouveauProfil],
       profilEnCours: nouveauProfil
     }));

     // Mettre à jour les paramètres pour utiliser ce profil
     mettreAJourParametres({
       styleBaleine: TypeStyleBaleine.PERSONNALISE,
       profilPersonnalise: nouveauProfil
     });

     // Fermer la modale après la sauvegarde
     setModalOuverte(false);
   }, [gererProfilsPersonnalises]);





  /**
   * Mise à jour des paramètres
   */
  const mettreAJourParametres = useCallback((nouveauxParametres: Partial<ParametresToit>) => {
    // LOG DE DÉBOGAGE POUR LES CHANGEMENTS DE PARAMÈTRES
    const logMessage = `🔧 [ToitModule] Mise à jour paramètres: ${JSON.stringify(nouveauxParametres)}`;
    // Log supprimé pour éviter le lag
    
    setEtat(prev => {
      const parametresMisAJour = {
        ...prev.parametres,
        ...nouveauxParametres
      };
      
      // LOG DES PARAMÈTRES FINAUX
      const logFinal = `📋 [ToitModule] Paramètres finaux: pointJonction=${parametresMisAJour.pointJonction}, rayonPointJonction=${parametresMisAJour.rayonPointJonction}, actif=${parametresMisAJour.actif}`;
      // Log supprimé pour éviter le lag
      
      // Générer immédiatement si le module est actif - suppression du délai
      if (parametresMisAJour.actif) {
        // Appel synchrone pour éviter le flash rectangulaire
        genererStructure(parametresMisAJour);
      }
      
      // Différer l'appel onParametresChange pour éviter l'erreur React
      setTimeout(() => {
        onParametresChange(parametresMisAJour);
      }, 0);
      
      return {
        ...prev,
        parametres: parametresMisAJour
      };
    });
  }, [onParametresChange, ecrireDansFichierLogs]);

  /**
   * Gestion du changement de profil personnalisé
   */
  const gererChangementProfil = useCallback((profil: ProfilBaleinePersonnalise) => {
    setEtat(prev => ({
      ...prev,
      profilEnCours: profil
    }));
    
    // Mettre à jour les paramètres avec le profil personnalisé
    mettreAJourParametres({
      styleBaleine: TypeStyleBaleine.PERSONNALISE,
      profilPersonnalise: profil
    });
  }, [mettreAJourParametres]);

  /**
   * Gestion des événements du canvas
   */
  const gererEvenementCanvas = useCallback((evenement: EvenementCanvas) => {
    // Traiter les événements spécifiques si nécessaire
  }, []);

   /**
    * Initialisation du module
    */
  useEffect(() => {
    const initialiser = () => {
      try {
        // Utiliser les styles prédéfinis locaux (conforme au cahier des charges - génération locale obligatoire)
         const stylesLocaux = obtenirStylesPredefinis();
         const profilsLocaux = gererProfilsPersonnalises();
         
         setEtat(prev => ({
           ...prev,
           stylesPredefinis: stylesLocaux,
           profilsPersonnalises: profilsLocaux
         }));
        
        // Initialiser le canvas si disponible
        if (canvasRef.current && !canvasInitialise) {
          canvas2DService.current.initialiser(canvasRef.current);
          canvas2DService.current.definirCallbackChangement(gererChangementProfil);
          canvas2DService.current.definirCallbackEvenement(gererEvenementCanvas);
          setCanvasInitialise(true);
        }
        
      } catch (error) {
        setEtat(prev => ({
          ...prev,
          erreurs: ['Erreur lors de l\'initialisation du module']
        }));
      }
    };

    initialiser();
   }, [canvasInitialise, obtenirStylesPredefinis, gererProfilsPersonnalises]);

  /**
   * Génération de la structure 3D
   * Tâche 4.1 et 4.2: Génération locale obligatoire
   */
  const genererStructure = useCallback((parametresAUtiliser?: ParametresToit) => {
    try {
      // Utiliser les paramètres passés en argument ou ceux de l'état
      const parametres = parametresAUtiliser || etat.parametres;
      
      // LOG DE DÉBOGAGE DIRECT DANS LE FICHIER
      const logMessage = `🏠 [ToitModule] genererStructure appelée avec: pointJonction=${parametres.pointJonction}, rayonPointJonction=${parametres.rayonPointJonction}, actif=${parametres.actif}`;
      // Log supprimé pour éviter le lag
      
      const structure = toit3DService.current.genererToit(
        parametres,
        donneesStructureBase
      );
      
      setEtat(prev => ({
        ...prev,
        structure: structure,
        structureGeneree: true,
        erreurs: []
      }));
      
      onStructureChange(structure);
      
    } catch (error) {
      setEtat(prev => ({
        ...prev,
        erreurs: ['Erreur lors de la génération de la structure 3D']
      }));
    }
  }, [etat.parametres, donneesStructureBase, onStructureChange]);



  // Mémorisation des valeurs sérialisées pour éviter les boucles infinies
  const positionsMontantsMemo = useMemo(() => 
    JSON.stringify(donneesStructureBase?.positionsMontants || []), 
    [donneesStructureBase?.positionsMontants]
  );
  
  const poutreHautePointsMemo = useMemo(() => 
    JSON.stringify(donneesStructureBase?.poutreHaute?.points || []), 
    [donneesStructureBase?.poutreHaute?.points]
  );
  
  const doublePoutrePointsMemo = useMemo(() => 
    JSON.stringify(donneesStructureBase?.doublePoutre?.points || []), 
    [donneesStructureBase?.doublePoutre?.points]
  );
  
  const centreMemo = useMemo(() => 
    JSON.stringify(donneesStructureBase?.centre || {}), 
    [donneesStructureBase?.centre]
  );

  /**
   * Synchronisation avec les changements de la structure de base
   * MISE À JOUR COMPLÈTE - Tous les paramètres de la structure de base déclenchent une régénération
   */
  useEffect(() => {
    if (donneesStructureBase) {
      // LOG DE DÉBOGAGE POUR TOUS LES CHANGEMENTS DE LA STRUCTURE DE BASE
      const logMessage = `🔄 [ToitModule] Changement structure de base détecté: forme=${donneesStructureBase.forme}, largeur=${donneesStructureBase.largeur}, longueur=${donneesStructureBase.longueur}, hauteur=${donneesStructureBase.hauteur}, materiau=${donneesStructureBase.materiau}, epaisseur=${donneesStructureBase.epaisseur}, couleur=${donneesStructureBase.couleur}`;
      // Log supprimé pour éviter le lag
      
      // Forcer la mise à jour des matériaux pour la couleur
      toit3DService.current.forcerMiseAJourMateriau(
        donneesStructureBase.couleur || '#8B4513',
        donneesStructureBase.materiau,
        donneesStructureBase.epaisseur || 20
      );
      
      // Synchroniser les paramètres avec la structure de base TOUJOURS
      // (pas seulement quand le module est actif)
      setEtat(prev => {
        const nouveauxParametres = {
          ...prev.parametres,
          materiau: donneesStructureBase.materiau,
          epaisseur: (donneesStructureBase.epaisseur || 20) / 1000, // Conversion mm vers m, défaut 20mm
          couleur: donneesStructureBase.couleur || '#8B4513'
        };
        
        // LOG DES NOUVEAUX PARAMÈTRES (réduit)
        console.log(`[ToitModule] Paramètres synchronisés: ${nouveauxParametres.materiau}, ${nouveauxParametres.epaisseur}m, ${nouveauxParametres.couleur}`);
        
        // Vérifier si les paramètres ont vraiment changé
        const parametresOntChange = 
          prev.parametres.materiau !== nouveauxParametres.materiau ||
          Math.abs((prev.parametres.epaisseur || 0) - nouveauxParametres.epaisseur) > 0.001 ||
          prev.parametres.couleur !== nouveauxParametres.couleur;
        
        if (parametresOntChange) {
          // LOG DE LA RÉGÉNÉRATION (réduit)
          console.log(`[ToitModule] Régénération - paramètres changés (actif: ${nouveauxParametres.actif})`);
          
          // Différer l'appel onParametresChange pour éviter l'erreur React
          setTimeout(() => {
            onParametresChange(nouveauxParametres);
          }, 0);
          
          // La génération sera gérée par le useEffect dédié aux paramètres
          // Éviter la double génération
          
          return { ...prev, parametres: nouveauxParametres };
        }
        
        // FORCER LA RÉGÉNÉRATION SEULEMENT SI NÉCESSAIRE
        // Éviter la double régénération si les paramètres ont déjà changé
        if (prev.parametres.actif && !parametresOntChange) {
          console.log(`[ToitModule] Régénération géométrique`);
          genererStructure(prev.parametres);
        }
        
        return prev;
      });
    }
  }, [
    // TOUTES les propriétés de la structure de base qui peuvent affecter le toit
    donneesStructureBase?.forme,
    donneesStructureBase?.largeur,
    donneesStructureBase?.longueur,
    donneesStructureBase?.hauteur,
    donneesStructureBase?.nombreMontants,
    donneesStructureBase?.materiau,
    donneesStructureBase?.epaisseur,
    donneesStructureBase?.couleur,
    // Changements dans les positions des montants (mémorisé)
    positionsMontantsMemo,
    // Changements dans la poutre haute (mémorisé)
    poutreHautePointsMemo,
    // Changements dans la double poutre
    donneesStructureBase?.doublePoutreActive,
    doublePoutrePointsMemo,
    // Changements dans le centre géométrique (mémorisé)
    centreMemo
    // Note: genererStructure et onParametresChange sont stables (useCallback)
  ]);

  /**
   * Génération automatique lors des changements
   */
  useEffect(() => {
    if (etat.parametres.actif) {
      genererStructure();
    } else {
      onStructureChange(null);
    }
  }, [
    etat.parametres.actif, 
    etat.parametres.styleBaleine, 
    etat.parametres.nombreBaleines, 
    etat.parametres.hauteurToit,
    etat.parametres.hauteurConvergence,
    etat.parametres.pointJonction,
    etat.parametres.rayonPointJonction,
    etat.parametres.aplatirBaleines,
    etat.parametres.materiau, 
    etat.parametres.epaisseur,
    etat.parametres.couleur,
    etat.parametres.profilPersonnalise,
    genererStructure,
    onStructureChange
  ]); // Ajout de etat.parametres.couleur aux dépendances

  /**
   * Chargement d'un profil personnalisé
   */
  const chargerProfilPersonnalise = (profil: ProfilBaleinePersonnalise) => {
    canvas2DService.current.chargerProfil(profil);
    setEtat(prev => ({
      ...prev,
      profilEnCours: profil
    }));
    
    // Mettre à jour les paramètres pour utiliser ce profil
    mettreAJourParametres({
      styleBaleine: TypeStyleBaleine.PERSONNALISE,
      profilPersonnalise: profil
    });
    
    // Fermer la modale après le chargement
    setModalOuverte(false);
  };



  /**
   * Rendu du panneau de configuration
   */
  const renderPanneauConfiguration = () => (
    <div className="toit-panneau-configuration">

      {etat.parametres.actif && (
        <>
          {/* Style de baleine */}
      <div style={controleGroupeStyles}>
        <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0e0', fontWeight: '500', fontSize: '13px' }}>Style des baleines :</label>
        <select
          value={etat.parametres.styleBaleine}
          onChange={(e) => {
            mettreAJourParametres({ 
              styleBaleine: e.target.value as TypeStyleBaleine,
              profilPersonnalise: e.target.value === TypeStyleBaleine.PERSONNALISE ? etat.profilEnCours : undefined
            });
          }}
          style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #555', borderRadius: '4px', fontSize: '14px', background: '#3a3a3a', color: '#e0e0e0', outline: 'none' }}
        >
          <option value={TypeStyleBaleine.CLASSIQUE}>Classique</option>
          <option value={TypeStyleBaleine.COURBE_DOUCE}>Courbe Douce</option>
          <option value={TypeStyleBaleine.GOTHIQUE}>Gothique</option>
          <option value={TypeStyleBaleine.MODERNE}>Moderne</option>
          <option value={TypeStyleBaleine.ASIATIQUE}>Asiatique</option>
          <option value={TypeStyleBaleine.PERSONNALISE}>Personnalisé</option>
        </select>
      </div>





      {/* Nombre de baleines */}
      <div style={controleGroupeStyles}>
        <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0e0', fontWeight: '500', fontSize: '13px' }}>Nombre de baleines : {etat.parametres.nombreBaleines}</label>
        <input
          type="range"
          min={3}
          max={50}
          step={1}
          value={etat.parametres.nombreBaleines}
          onChange={(e) => mettreAJourParametres({ nombreBaleines: parseInt(e.target.value) })}
          style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', height: '6px', borderRadius: '3px', background: '#dee2e6', outline: 'none', opacity: '0.8', cursor: 'pointer' }}
        />
        <small style={{ display: 'block', marginTop: '4px', color: '#6c757d', fontSize: '0.75rem' }}>Indépendant du nombre de montants - Positionnement intelligent aux coins</small>
      </div>

      {/* Aligner les baleines sur les montants */}
      <div style={controleGroupeStyles}>
        <label style={{ display: 'flex', alignItems: 'center', color: '#e0e0e0', fontWeight: '500', fontSize: '13px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={etat.parametres.alignerSurMontants}
            onChange={(e) => mettreAJourParametres({ alignerSurMontants: e.target.checked })}
            style={{ marginRight: '8px', cursor: 'pointer' }}
          />
          Aligner les baleines sur les montants
        </label>
        <small style={{ display: 'block', marginTop: '4px', color: '#6c757d', fontSize: '0.75rem' }}>Si activé, le nombre et la position des baleines suivent exactement les montants</small>
      </div>

      {/* Hauteur du toit */}
      <div style={controleGroupeStyles}>
        <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0e0', fontWeight: '500', fontSize: '13px' }}>Hauteur du toit : {etat.parametres.hauteurToit.toFixed(1)}m</label>
        <input
          type="range"
          min="0.1"
          max="1.5"
          step="0.05"
          value={etat.parametres.hauteurToit}
          onChange={(e) => mettreAJourParametres({ hauteurToit: parseFloat(e.target.value) })}
          style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', height: '6px', borderRadius: '3px', background: '#dee2e6', outline: 'none', opacity: '0.8', cursor: 'pointer' }}
        />
        <small style={{ display: 'block', marginTop: '4px', color: '#6c757d', fontSize: '0.75rem' }}>Hauteur générale de la structure du toit</small>
      </div>

      {/* Hauteur de convergence */}
      <div style={controleGroupeStyles}>
        <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0e0', fontWeight: '500', fontSize: '13px' }}>Hauteur de convergence : {etat.parametres.hauteurConvergence.toFixed(1)}m</label>
        <input
          type="range"
          min="0.1"
          max="1.5"
          step="0.05"
          value={etat.parametres.hauteurConvergence}
          onChange={(e) => mettreAJourParametres({ hauteurConvergence: parseFloat(e.target.value) })}
          style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', height: '6px', borderRadius: '3px', background: '#dee2e6', outline: 'none', opacity: '0.8', cursor: 'pointer' }}
        />
        <small style={{ display: 'block', marginTop: '4px', color: '#6c757d', fontSize: '0.75rem' }}>Hauteur du point de convergence central</small>
      </div>

      {/* Aplatissement des baleines */}
      <div style={controleGroupeStyles}>
        <label style={{ display: 'flex', alignItems: 'center', color: '#e0e0e0', fontWeight: '500', fontSize: '13px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={etat.parametres.aplatirBaleines}
            onChange={(e) => mettreAJourParametres({ aplatirBaleines: e.target.checked })}
            style={{ marginRight: '8px', cursor: 'pointer' }}
          />
          Aplatir les baleines selon la hauteur
        </label>
        <small style={{ display: 'block', marginTop: '4px', color: '#6c757d', fontSize: '0.75rem' }}>Contrôle la courbure avec le curseur "Hauteur du toit"</small>
      </div>



      {/* Point de jonction */}
      <div style={controleGroupeStyles}>
        <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0e0', fontWeight: '500', fontSize: '13px' }}>Point de jonction :</label>
        <select
          value={etat.parametres.pointJonction}
          onChange={(e) => mettreAJourParametres({ pointJonction: e.target.value as TypePointJonction })}
          style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #555', borderRadius: '4px', fontSize: '14px', background: '#3a3a3a', color: '#e0e0e0', outline: 'none' }}
        >
          <option value={TypePointJonction.POINT_SIMPLE}>Point Simple</option>
          <option value={TypePointJonction.POUTRE_CIRCULAIRE}>Poutre Circulaire</option>
        </select>
      </div>

      {/* Rayon du point de jonction */}
      {etat.parametres.pointJonction !== TypePointJonction.POINT_SIMPLE && (
        <div style={controleGroupeStyles}>
          <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0e0', fontWeight: '500', fontSize: '13px' }}>Rayon du point de jonction : {(etat.parametres.rayonPointJonction || 0.1).toFixed(2)}m</label>
          <input
            type="range"
            min="0.05"
            max="0.5"
            step="0.01"
            value={etat.parametres.rayonPointJonction || 0.1}
            onChange={(e) => mettreAJourParametres({ rayonPointJonction: parseFloat(e.target.value) })}
            style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', height: '6px', borderRadius: '3px', background: '#dee2e6', outline: 'none', opacity: '0.8', cursor: 'pointer' }}
          />
        </div>
      )}
        </>
      )}

       {/* Boutons d'action */}
      <div className="toit-actions">
        <button 
          onClick={() => setModalOuverte(true)}
          className="toit-btn-primaire"
        >
          Éditeur 2D
        </button>
      </div>
    </div>
  );

  /**
   * Rendu de l'éditeur 2D
   */
  const renderEditeur2D = () => (
    <div className="toit-editeur-2d">
      <h3>Éditeur de Profil Personnalisé</h3>
      
      {/* Barre d'outils */}
      <div className="toit-barre-outils">
        <button
          className={`toit-outil ${etat.outilCanvas === OutilDessin.CRAYON ? 'actif' : ''}`}
          onClick={() => {
            canvas2DService.current.definirOutil(OutilDessin.CRAYON);
            setEtat(prev => ({ ...prev, outilCanvas: OutilDessin.CRAYON }));
          }}
        >
          ✏️ Crayon
        </button>
        <button
          className={`toit-outil ${etat.outilCanvas === OutilDessin.LIGNE ? 'actif' : ''}`}
          onClick={() => {
            canvas2DService.current.definirOutil(OutilDessin.LIGNE);
            setEtat(prev => ({ ...prev, outilCanvas: OutilDessin.LIGNE }));
          }}
        >
          📏 Ligne
        </button>
        <button
          className={`toit-outil ${etat.outilCanvas === OutilDessin.BEZIER ? 'actif' : ''}`}
          onClick={() => {
            canvas2DService.current.definirOutil(OutilDessin.BEZIER);
            setEtat(prev => ({ ...prev, outilCanvas: OutilDessin.BEZIER }));
          }}
        >
          🎨 Courbe
        </button>
        <button
          className={`toit-outil ${etat.outilCanvas === OutilDessin.SELECTION ? 'actif' : ''}`}
          onClick={() => {
            canvas2DService.current.definirOutil(OutilDessin.SELECTION);
            setEtat(prev => ({ ...prev, outilCanvas: OutilDessin.SELECTION }));
          }}
        >
          👆 Sélection
        </button>
        
        <div className="toit-separateur"></div>
        
        <button
          onClick={() => canvas2DService.current.annuler()}
          className="toit-btn-action"
        >
          ↶ Annuler
        </button>
        <button
          onClick={() => canvas2DService.current.refaire()}
          className="toit-btn-action"
        >
          ↷ Refaire
        </button>
        <button
          onClick={() => canvas2DService.current.effacer()}
          className="toit-btn-action"
        >
          🗑️ Effacer
        </button>
      </div>

      {/* Canvas de dessin */}
      <div className="toit-canvas-container">
        <canvas
          ref={canvasRef}
          className="toit-canvas"
        />
      </div>

      {/* Actions de sauvegarde */}
      <div className="toit-actions-profil">
        <input
          type="text"
          placeholder="Nom du profil"
          className="toit-input-nom"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              const nom = (e.target as HTMLInputElement).value.trim();
              if (nom && etat.profilEnCours) {
                sauvegarderProfilPersonnalise(nom, etat.profilEnCours);
                (e.target as HTMLInputElement).value = '';
              }
            }
          }}
        />
        <button
          onClick={() => {
            const input = document.querySelector('.toit-input-nom') as HTMLInputElement;
            const nom = input?.value.trim();
            if (nom && etat.profilEnCours) {
              sauvegarderProfilPersonnalise(nom, etat.profilEnCours);
              input.value = '';
            }
          }}
          className="toit-btn-sauvegarder"
        >
          💾 Sauvegarder
        </button>
      </div>

      {/* Liste des profils sauvegardés */}
      {etat.profilsPersonnalises.length > 0 && (
        <div className="toit-profils-sauvegardes">
          <h4>Profils Sauvegardés</h4>
          <div className="toit-liste-profils">
            {etat.profilsPersonnalises.map((item) => (
              <div key={item.id} className="toit-profil-item">
                <span>{item.nom}</span>
                <button
                  onClick={() => chargerProfilPersonnalise(item)}
                  className="toit-btn-charger"
                >
                  📂 Charger
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  /**
   * Rendu des messages d'erreur et d'avertissement
   */
  const renderMessages = () => (
    <>
      {etat.erreurs.length > 0 && (
        <div className="toit-messages toit-erreurs">
          <h4>❌ Erreurs</h4>
          <ul>
            {etat.erreurs.map((erreur, index) => (
              <li key={index}>{erreur}</li>
            ))}
          </ul>
        </div>
      )}
      
      {etat.avertissements.length > 0 && (
        <div className="toit-messages toit-avertissements">
          <h4>⚠️ Avertissements</h4>
          <ul>
            {etat.avertissements.map((avertissement, index) => (
              <li key={index}>{avertissement}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  /**
   * Nettoyage lors du démontage
   */
  useEffect(() => {
    return () => {
      toit3DService.current.dispose();
      canvas2DService.current.dispose();
    };
  }, []);
  // Styles en ligne pour correspondre au module soudures
  const controleGroupeStyles = {
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px'
  };

  const controleLabelStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#e0e0e0'
  };

  useEffect(() => {
    if (
      etat.parametres.styleBaleine === TypeStyleBaleine.PERSONNALISE &&
      !modalOuverte &&
      !etat.parametres.profilPersonnalise
    ) {
      setModalOuverte(true);
    }
  }, [etat.parametres.styleBaleine, etat.parametres.profilPersonnalise, modalOuverte]);

  return (
    <div className="toit-module">
      <div>
        <h3>🏠 Module Toit</h3>
        <p style={{ margin: '0 0 10px 0', color: '#aaa', fontSize: '12px' }}>Configuration du toit de la tonnelle</p>
      </div>
      
      {/* Activation/Désactivation */}
      <div style={controleGroupeStyles}>
        <label style={controleLabelStyles}>
          <input
            type="checkbox"
            checked={etat.parametres.actif}
            onChange={(e) => mettreAJourParametres({ actif: e.target.checked })}
          />
          Activer le toit
        </label>
      </div>



      {renderMessages()}

      <div className="toit-contenu">
        <div className="toit-panneau-gauche">
          {renderPanneauConfiguration()}
        </div>

        {/* Éditeur inline désactivé : on utilise la modale */}
        {/* {etat.modeEdition && (
        <div className="toit-panneau-droite">
          {renderEditeur2D()}
        </div>
      )} */}
      {modalOuverte && (
        <ModalEditeurProfil
          isOpen={modalOuverte}
          onClose={() => setModalOuverte(false)}
          onSauvegarder={sauvegarderProfilPersonnalise}
          profilsPersonnalises={etat.profilsPersonnalises}
          onChargerProfil={chargerProfilPersonnalise}
          onSupprimerProfil={(id) => {
            const profilsMisAJour = etat.profilsPersonnalises.filter(p => p.id !== id);
            localStorage.setItem('toit-profils-personnalises', JSON.stringify(profilsMisAJour));
            setEtat(prev => ({ ...prev, profilsPersonnalises: profilsMisAJour }));
          }}
        />
      )}
    </div>
  </div>
);

};

export default ToitModule;